import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { AggridDiv, aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle } from "src/components/common/common";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

import { ColDef, PostSortRowsParams } from "ag-grid-community";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useRowSelectionHandler, getSwitchButtonColumnOptions, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell  } from "src/utils/grid/gridStyleUtils";
import { SYS3010_R01Dto } from "./Dto_SYS3010_R01";
import { useSYS3010_R01Store } from "./Store_SYS3010_R01";
import { CompCdColumn, WorkPlaceColumn } from "src/utils/grid/GridColumnContent";
import { useSYS3010_R01Modal } from "./hooks/useSYS3010_R01Modal";
import { useSYS3010_R01CellChange } from "./hooks/SYS3010_R01CellChange";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

const COLUMN_STATE_KEY = 'SYS3010_R01-grid-column-state';

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<SYS3010_R01Dto> | null>;
	defaultSYS3010_R01Row: SYS3010_R01Dto;
}

export const Table_SYS3010_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef, defaultSYS3010_R01Row } = props;

	const {
		SYS3010_R01List, setSYS3010_R01List,
		insertSYS3010_R01List, removeSYS3010_R01List, updateSYS3010_R01List,
		deletedSYS3010_R01List, addDeletedSYS3010_R01List,
		selectedSYS3010_R01List, addSelectedSYS3010_R01List, removeSelectedSYS3010_R01List,
		initSelectedSYS3010_R01List, allSelectedSYS3010_R01List,
		originalSYS3010_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useSYS3010_R01Store();
	const { compCdList, orgCdList, workPlaceList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SYS3010_R01List,
		defaultGridData: defaultSYS3010_R01Row,
		selectedRow: selectedSYS3010_R01List,
		originalGridData: originalSYS3010_R01List,
		updateRow: (index: number, data: any) => updateSYS3010_R01List(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		userId: '사용자 ID',
		userNm: '사용자명',
		empNo: '사원번호',
		empNm: '성명',
		compCd: '사업장명',
		orgNm: '부서명',
		workplaceCd: '기본공정',
		autGroupCd: '메뉴권한',
		autGroupNm: '메뉴권한명',
		costAut: '원가권한',
		approveAut: '승인권한',
		deleteAut: '삭제권한',
		lockSts: '잠금상태',
		password: '비밀번호',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['empNm'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['userId'],
		
		pointerCells: ['userId', 'userNm', 'empNo', 'empNm', 'compCd', 'orgNm', 'workplaceCd', 'autGroupCd', 'autGroupNm', 'costAut', 'approveAut', 'deleteAut', 'lockSts', 'password'],
		insertedPointerCells: ['userId'],
		commonPointerCells: [''],
		
		alignLeftCells: ['empNo', 'compCd']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<SYS3010_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedSYS3010_R01List,
            deletedList: deletedSYS3010_R01List,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [selectedSYS3010_R01List, deletedSYS3010_R01List, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns, createCellClassRules]);

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = ['userId', 'userNm', 'costAut', 'compCd', 'workplaceCd', 'approveAut', 'deleteAut', 'lockSts', 'password', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['userId', 'userNm', 'password'];

	const { settingCodeSearchModalProps } = useSYS3010_R01Modal({
		updateSYS3010_R01List,
		gridRef,
		compCdList,
		orgCdList,
	});

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: SYS3010_R01List,
			rowData: SYS3010_R01List[rowIndex],
			insertRow: insertSYS3010_R01List,
			defaultRowData: defaultSYS3010_R01Row,
			setClickable
		});
	}, [SYS3010_R01List, insertSYS3010_R01List, defaultSYS3010_R01Row, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() =>
		createGridDropDownCommitAfter<SYS3010_R01Dto>(defaultEditableColumns)
	, [defaultEditableColumns]);

	const baseColumnDefs: ColDef<SYS3010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: SYS3010_R01Dto) => renderIndexCell(
				params, 
				(innerParams: SYS3010_R01Dto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedSYS3010_R01List,
						selectedList: selectedSYS3010_R01List,
						dataList: SYS3010_R01List
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					SYS3010_R01List, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeSYS3010_R01List, 
					(valueIndex) => addDeletedSYS3010_R01List([valueIndex.toString()])
				),
			)
		),
        {
            headerName: '사용자 ID', colId: 'userId', field: 'userId', width: 100,
            editable: (params) => {
                // 해당 행이 추가된(신규) 행일 때만 편집 가능
                return params.data?.inserted === true;
            },
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '사용자명', colId: 'userNm', field: 'userNm', width: 110,
            editable: true,
        },
        {
            headerName: '사원번호', colId: 'empNo', field: 'empNo', width: 100,
            editable: false,
        },
        {
            headerName: '성명', colId: 'empNm', field: 'empNm', width: 80,
            editable: false,
        },
        {
            headerName: '사업장명', colId: 'compCd', field: 'compCd', width: 120,
            editable: true,
			...CompCdColumn.options(compCdList, true, CompCdColumn.change(updateSYS3010_R01List), handleGridDropDownCommitAfter),
        },
        {
            headerName: '부서명', colId: 'orgNm', field: 'orgNm', width: 80,
            editable: false,
        },
        {
            headerName: '기본공정', colId: 'workplaceCd', field: 'workplaceCd', width: 120,
            editable: true,
			...WorkPlaceColumn.options(
				workPlaceList.filter(opt => opt.useYn === 'Y'),
				true,
				(params) => params.data?.compCd,
				WorkPlaceColumn.change(updateSYS3010_R01List),
				handleGridDropDownCommitAfter,
				{ value: '', label: '(미입력)' },
			),
        },
        {
            headerName: '메뉴권한', colId: 'autGroupCd', field: 'autGroupCd', width: 80,
            editable: false,
        },
		{
			headerName: '메뉴권한명', colId: 'autGroupNm', field: 'autGroupNm', width: 120,
			editable: false,
		},
        {
            headerName: '원가권한', colId: 'costAut', field: 'costAut', width: 100,
			...getSwitchButtonColumnOptions('costAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: '승인권한', colId: 'approveAut', field: 'approveAut', width: 100,
			...getSwitchButtonColumnOptions('approveAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: '삭제권한', colId: 'deleteAut', field: 'deleteAut', width: 100,
			...getSwitchButtonColumnOptions('deleteAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: '잠금상태', colId: 'lockSts', field: 'lockSts', width: 100,
			...getSwitchButtonColumnOptions('lockSts', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: '비밀번호', colId: 'password', field: 'password', width: 110,
            editable: true,
            cellRenderer: (params: any) => {
                // 조회 시 비밀번호는 빈 값이지만, UI에서는 항상 마스킹 표시
                // 실제 비밀번호가 있든 없든 항상 표시하여 보안성 유지
                return (
                    <AggridDiv>
                        {'•'.repeat(8)}
                    </AggridDiv>
                );
            },
            valueParser: (params) => { return params.newValue;},
            cellEditor: 'agTextCellEditor',
            cellEditorParams: {
                maxLength: 12
            },
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		// ...IndexParameterCell(),

	], [deletedSYS3010_R01List, SYS3010_R01List, orgCdList, compCdList, workPlaceList, createCellClassRules]);



	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<SYS3010_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SYS3010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, columnState, headerNames]);

	const { handleCellChange } = useSYS3010_R01CellChange({
		updateSYS3010_R01List,
		originalSYS3010_R01List,
	});

    const handleRowClick = useRowSelectionHandler({
        dataList: SYS3010_R01List,
        idKey: 'valueIndex',
        selectedList: selectedSYS3010_R01List,
        deletedList: deletedSYS3010_R01List,
        addSelected: addSelectedSYS3010_R01List,
        removeSelected: removeSelectedSYS3010_R01List,
        clickable, setClickable
    });

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: SYS3010_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	const cellClickActions: CustomCellClickAction<SYS3010_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [settingCodeSearchModalProps]);
	const handleCellClick = CreateCellClickHandler(SYS3010_R01List, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<SYS3010_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'empNo' || field === 'empNm' || field === 'orgNm',
			action: (e, foundData) => {
				settingCodeSearchModalProps('사원번호', foundData.empNo!, false, 'empNo', foundData, foundData.valueIndex!);
			}
		},
		{
			validate: (field, data) => field === 'autGroupCd' || field === 'autGroupNm',
			action: (e, foundData) => {
				settingCodeSearchModalProps('메뉴권한', foundData.autGroupCd!, false, 'autGroupCd', foundData, foundData.valueIndex!);
			}
		},
	], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(SYS3010_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<SYS3010_R01Dto>) => {
		if (isRowDraggingRef.current) return;
		postSortHandler(params, SYS3010_R01List, originalSYS3010_R01List, setSYS3010_R01List);
	}, [SYS3010_R01List, originalSYS3010_R01List]);

	const isRowDraggingRef = useRef(false);

	// const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<SYS3010_R01Dto>) => {
	// 	// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
	// 	if (isRowDraggingRef.current) return;
	// 	postSortHandler(params, SYS3010_R01List, originalSYS3010_R01List, setSYS3010_R01List);
	// }, [SYS3010_R01List, originalSYS3010_R01List, ]);
	
	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		userId: SYS3010_R01List.length.toLocaleString() + "건",
		editable: false,
	}]), [SYS3010_R01List]);

    return (
		<GridContextWrapper<SYS3010_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setSYS3010_R01List}
			initSelected={initSelectedSYS3010_R01List}
			allSelected={allSelectedSYS3010_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
			defaultRowData={defaultSYS3010_R01Row}
		>
			<Grid_Excel<SYS3010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<SYS3010_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSYS3010_R01List,
					deletedList: deletedSYS3010_R01List
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}
                rowDragManaged={true}
                rowDragEntireRow={true}
				onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                onRowDragEnd={(e: any) => {
                    const api = e.api;
                    // 정렬 해제하여 사용자가 정한 순서를 유지
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }
                    const reordered: SYS3010_R01Dto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,
                            orderIndexSeq: 0,
                        });
                    });
                    setSYS3010_R01List(reordered);
					isRowDraggingRef.current = false;
                }}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				
			/>
		</GridContextWrapper>
	)
}
